import { injectable } from 'inversify';
import { observable, action, computed, runInAction } from 'mobx';
import { JobsApiService, VisitAssistantApiService, FormsApiService, JobStatus } from 'src/api';
import { FormSignatureRequestModel } from 'src/api/model/formSignatureRequestModel';
import { InvoiceSignatureRequestModel } from 'src/api/model/invoiceSignatureRequestModel';
import { ProjectSignatureRequestModel } from 'src/api/model/projectSignatureRequestModel';
import { ProjectSignatureRequest } from 'src/api/model/projectSignatureRequest';
import { VisitActionRequest } from 'src/api/model/visitActionRequest';
import { VisitActionRequestTimeZone } from 'src/api/model/visitActionRequestTimeZone';
import { SignInvoiceRequest } from 'src/api/model/signInvoiceRequest';
import { SignFormRequest } from 'src/api/model/signFormRequest';

export type SignatureDocument =
    | FormSignatureRequestModel
    | InvoiceSignatureRequestModel
    | ProjectSignatureRequestModel;
export type SignDocumentRequest = SignFormRequest | SignInvoiceRequest | ProjectSignatureRequest;

@injectable()
export class SignatureStore {
    @observable businessUnitOfficialName?: string;
    @observable tenantTimeZone?: VisitActionRequestTimeZone;
    @observable jobIsActive: boolean;
    @observable estimateBundleId?: string | null;
    @observable showSidebar: boolean;
    @observable currentCustomFormName?: string | null;

    private documentsInitialized: boolean;
    private jobId?: number;
    private jobStatus?: JobStatus;
    private visitAssistantId?: string;
    private documentsPollingInterval?: any;
    private documentsPollingIntervalTime = 10000;

    constructor(
        private jobsApi: JobsApiService,
        private visitAssistantApi: VisitAssistantApiService,
        private formsApi: FormsApiService
    ) {
        this.documentsInitialized = false;
        this.jobIsActive = true;
        this.showSidebar = false;
    }

    @action
    async getVisitActionRequest(visitAssistantId?: string) {
        if (!visitAssistantId) return;

        if (!this.jobId || this.visitAssistantId !== visitAssistantId) {
            this.visitAssistantId = visitAssistantId;
            const showBusy = !this.documents.length;
            let data: VisitActionRequest;
            data = (await this.visitAssistantApi.getVisitActionRequest(visitAssistantId, showBusy))
                .data;
            const { jobId, businessUnitOfficialName, tenantTimeZone, estimateBundleId, jobStatus } =
                data;
            this.setJobId(jobId);
            this.setEstimateBundleId(estimateBundleId);
            this.setJobStatus(jobStatus);
            this.setShowSidebar(estimateBundleId);

            runInAction(() => {
                this.businessUnitOfficialName = businessUnitOfficialName;
                this.tenantTimeZone = tenantTimeZone;
            });
        }
    }

    private async getJobStatus(visitAssistantId: string) {
        return (
            await this.visitAssistantApi.getVisitActionRequest(
                visitAssistantId,
                !this.documents.length
            )
        ).data.jobStatus;
    }

    async startDocumentsPolling() {
        if (!this.jobId) {
            return;
        }

        await this.loadDocuments();
        if (!this.isJobCompleted()) {
            this.documentsPollingInterval = setInterval(
                this.loadDocuments,
                this.documentsPollingIntervalTime
            );
        }
    }

    stopDocumentsPolling() {
        window.clearInterval(this.documentsPollingInterval);
    }

    setJobId(jobId: number) {
        this.jobId = jobId;
    }

    @action
    setEstimateBundleId(estimateBundleId?: string | null) {
        this.estimateBundleId = estimateBundleId;
    }

    setJobStatus(jobStatus: JobStatus) {
        this.jobStatus = jobStatus;
    }

    @action
    setShowSidebar(estimateBundleId?: string | null) {
        this.showSidebar = !!estimateBundleId;
    }

    @computed
    get documents(): SignatureDocument[] {
        const signatureRequest = this.jobIsActive
            ? this.jobsApi.getSignatureRequestsForJobObservable
            : null;

        if (
            signatureRequest &&
            (this.jobStatus === JobStatus.NUMBER_2 || this.isOnlineEstimateCompleted)
        ) {
            const { formSignatureRequests, invoiceSignatureRequests, projectSignatureRequests } =
                signatureRequest;
            return [
                ...formSignatureRequests!,
                ...invoiceSignatureRequests!,
                ...projectSignatureRequests!,
            ];
        }

        return [];
    }

    @computed
    get isOnlineEstimateCompleted() {
        return this.estimateBundleId && this.isJobCompleted();
    }

    @action
    setCurrentFormName = (formName?: string) => {
        this.currentCustomFormName = formName;
    };

    async fetchFormName(submittingId: string) {
        const resp = await this.formsApi.getFormName(submittingId, this.visitAssistantId);
        this.setCurrentFormName(resp.data.formName);
    }

    isJobCompleted() {
        return this.jobStatus === JobStatus.NUMBER_4; // Completed
    }

    loadDocuments = async () => {
        if (!this.jobId) {
            this.documentsInitialized = true;
            return;
        }

        const showBusy = !this.documentsInitialized;
        try {
            await this.jobsApi.getSignatureRequestsForJob(this.jobId, showBusy, {
                400: () => {
                    runInAction(() => {
                        this.jobIsActive = false;
                    });
                    this.stopDocumentsPolling();
                    return '400 Bad Request';
                },
            });
        } catch (error) {
            console.warn('Get signature requests for job:', error);
        }
        this.documentsInitialized = true;

        if (this.visitAssistantId) {
            this.setJobStatus(await this.getJobStatus(this.visitAssistantId));
            if (this.isJobCompleted()) {
                this.stopDocumentsPolling();
            }
        }
    };
}
